import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface AddDailySpendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaignName: string;
  companyId: string;
}

export function AddDailySpendDialog({ 
  open, 
  onOpenChange, 
  campaignId, 
  campaignName,
  companyId 
}: AddDailySpendDialogProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { formatDate } = useTimezoneUtils();
  const { locale } = useCompanyLocale();
  const [date, setDate] = useState<Date>(new Date());
  const [actualCost, setActualCost] = useState('');
  const [impressions, setImpressions] = useState('');
  const [clicks, setClicks] = useState('');
  const [conversions, setConversions] = useState('');

  const addSpend = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('campaign_daily_spend')
        .upsert({
          campaign_id: campaignId,
          company_id: companyId,
          spend_date: formatDate(date, 'yyyy-MM-dd'),
          actual_cost: parseFloat(actualCost) || 0,
          impressions: impressions ? parseInt(impressions) : null,
          clicks: clicks ? parseInt(clicks) : null,
          conversions: conversions ? parseInt(conversions) : null,
          source_platform: 'manual',
          entered_by: user?.id,
          currency: locale.currency,
        }, {
          onConflict: 'campaign_id,spend_date'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Daily spend added successfully');
      queryClient.invalidateQueries({ queryKey: ['campaigns-with-costs'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-daily-spend'] });
      handleClose();
    },
    onError: (error) => {
      toast.error('Failed to add daily spend: ' + error.message);
    },
  });

  const handleClose = () => {
    setDate(new Date());
    setActualCost('');
    setImpressions('');
    setClicks('');
    setConversions('');
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!actualCost) {
      toast.error('Actual cost is required');
      return;
    }
    addSpend.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Manual Daily Spend</DialogTitle>
          <p className="text-sm text-muted-foreground">{campaignName}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? formatDate(date, "PPP") : <span>Pick a date</span>}
                  </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => newDate && setDate(newDate)}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="actual_cost">Actual Cost ({locale.currency}) *</Label>
            <Input
              id="actual_cost"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={actualCost}
              onChange={(e) => setActualCost(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="impressions">Impressions</Label>
              <Input
                id="impressions"
                type="number"
                placeholder="0"
                value={impressions}
                onChange={(e) => setImpressions(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clicks">Clicks</Label>
              <Input
                id="clicks"
                type="number"
                placeholder="0"
                value={clicks}
                onChange={(e) => setClicks(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="conversions">Conversions</Label>
              <Input
                id="conversions"
                type="number"
                placeholder="0"
                value={conversions}
                onChange={(e) => setConversions(e.target.value)}
              />
            </div>
          </div>

          <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground">
            <p className="font-medium mb-1">Manual Entry</p>
            <p>This data will be marked as manually entered and can be edited later.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={addSpend.isPending}>
              {addSpend.isPending ? 'Adding...' : 'Add Spend'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
